import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TutorForm from "./TutorForm";
import CommonForm from "./CommonForm";
import {
  fetchFormSizesData,
  fetchMissingSizes,
  loginTutor,
} from "../../services/api.js";
import Loader from "./Loader";
import logoBlanco from "../../images/logoBlanco.png";
import TutorFormStyles from "../../styles/TutorForm.module.css";
import catalogoEmojis from "../../images/catalogo-emojis-new.webp";
import swal from "sweetalert2";
import logoAzul from "../../images/logoAzul.png";

const FormSizes = () => {
  const { order_id, is_tutor } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [missingSizes, setMissingSizes] = useState({});
  const [isMobileView, setIsMobileView] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const decoded = atob(order_id);
        const parsedOrderId = JSON.parse(decoded);
        const tutorBoolDecoded = atob(is_tutor);

        setIsTutor(tutorBoolDecoded === "true");

        const [formDataRes, missingSizesRes] = await Promise.all([
          fetchFormSizesData(parsedOrderId),
          fetchMissingSizes(parsedOrderId),
        ]);

        setFormData(formDataRes);
        setMissingSizes(missingSizesRes);
      } catch (error) {
        console.error("Error al obtener datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [order_id, is_tutor, reloadKey]);

  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
  
    checkViewport();
    window.addEventListener('resize', checkViewport);
  
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  if (loading) return <Loader />;
  if (!formData || formData.length === 0 || !missingSizes)
    return (
      <div
        style={{
          backgroundColor: "#f0f8ff",
          border: "2px dashed #0071ce",
          borderRadius: "12px",
          padding: "1.5rem",
          textAlign: "center",
          color: "#0071ce",
          fontSize: "1.1rem",
          marginTop: "1rem",
        }}
      >
        <i
          className="bi bi-emoji-frown"
          style={{ fontSize: "2rem", marginBottom: "0.5rem" }}
        ></i>
        <p style={{ margin: 0 }}>
          ¡Ups! No encontramos datos para mostrar. <br />
          ¿Estás seguro de haber elegido bien?
        </p>
      </div>
    );

  const data = formData;

  const nextSection = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 1500);
    setConfirmed(true);
  };

  const nicknameTooltipText = () => {
    swal.fire({
      html: `
<p><i class="bi bi-exclamation-triangle-fill"></i> El emoji solo puede ir <strong>ADELANTE Y/O ATRAS del apodo, NO EN MEDIO.</strong></p>
<p><i class="bi bi-x" style="color: #e658c5;"></i> Si no quieres que lleve apodo, deja este campo vacío. <i class="bi bi-x" style="color: #e658c5;"></i></p>
<p><strong>👻 Los emojis pueden llegar a tener un costo extra, 🌈 <br/>consultalo con tu asesor/a.</strong></p>
<img src=${catalogoEmojis} alt="Emojis disponibles" style="width:100%;max-width:900px;margin-top:10px;" />
`,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#0071ce",
    });
  };

  const initialsTooltipText = () => {
    swal.fire({
      html: `
      <span>
      En caso que tu prenda lleve algo más personalizado
      aclaralo en este campo!
      <br />
      <strong>Ej. modalidad, opcionales, números
      personalizados, etc.</strong>
    </span>
`,
      confirmButtonText: "Entendido",
      confirmButtonColor: "#0071ce",
    });
  };

  const handleTutorLogin = async () => {
    const { value: dni } = await swal.fire({
      title: "Acceso para tutor o delegado",
      input: "text",
      inputLabel: "Ingresá tu DNI",
      inputPlaceholder: "Sin puntos ni espacios",
      showCancelButton: true,
      confirmButtonText: "Validar",
      confirmButtonColor: "#0071ce",
    });

    if (!dni) return;

    const customersWithPassword = data.customers
      .filter((c) => !!c.password)
      .map((c) => ({
        id: c.id,
        password: c.password,
      }));

    try {
      const { ok } = await loginTutor(dni, customersWithPassword);

      if (!ok) {
        swal.fire({
          title: "Error",
          text: "Credenciales inválidas. Por favor, revisá tu DNI.",
          icon: "error",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#0071ce",
        });
        return;
      }

      setIsTutor(true);
      navigate(
        `/form-sizes/${btoa(JSON.stringify(data.order.id))}/${btoa("true")}`
      );
    } catch (error) {
      console.error("Login tutor error:", error);
      swal.fire("Error", "Hubo un problema al validar", "error");
    }
  };

  if (!confirmed) {
    return (
      <div className={TutorFormStyles.confirmWrapper}>
        <div className={TutorFormStyles.confirmCard}>
          <img src={logoAzul} alt="Little blue logo" className="w-25 mb-5" />
          {isTutor ? <h2>Carga de Talles Tutor</h2> : <h2>Carga de Talles</h2>}
          <ul>
            <li>
              <strong>
                <i className="bi bi-file-text-fill"></i> Pedido{" "}
              </strong>
              <span
                className={TutorFormStyles.pinkTitle}
                style={{ fontWeight: "bold" }}
              >
                #{data.order?.code}
              </span>
            </li>
            <li>
              <i className="bi bi-bank2"></i> {data.school?.name}
              {data.school?.alias && ` (${data.school.alias})`}
            </li>
            <li>
              <i className="bi bi-geo-alt-fill"></i>{" "}
              {data.school?.address || "Dirección no especificada"}
            </li>
          </ul>
          <div className="d-grid gap-3">
            {!isTutor && (
              <button
                className={`${TutorFormStyles.confirmButton}`}
                onClick={handleTutorLogin}
                style={{ backgroundColor: "#00509d" }}
              >
                <i className="bi bi-person-badge-fill"></i> Soy tutor o delegado
              </button>
            )}
            {isTutor ? (
              <button
                className={TutorFormStyles.confirmButton}
                onClick={nextSection}
              >
                Continuar <i className="bi bi-arrow-right-circle"></i>
              </button>
            ) : (
              <button
                className={TutorFormStyles.confirmButton}
                onClick={nextSection}
              >
                Soy alumno regular <i className="bi bi-arrow-right-circle"></i>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className={`${TutorFormStyles.barraLogo}`}>
        <img
          src={logoBlanco}
          alt="Little blue white logo"
          style={{ width: "70px" }}
        />
      </section>
      <div
        className="container"
        style={{ marginTop: "40px", fontFamily: "Gotham Medium" }}
      >
        <div className="mb-3">
          <h5>
            <i className="bi bi-file-text-fill"></i>{" "}
            <strong>
              Pedido{" "}
              <span className={TutorFormStyles.pinkTitle}>
                #{data.order?.code}
              </span>
            </strong>
          </h5>
          <h6 style={{ fontFamily: "Gotham Medium" }}>
            <i className="bi bi-bank2"></i> {data.school?.name}
            {data.school?.alias && ` (${data.school.alias})`}
          </h6>
          <h6 style={{ fontFamily: "Gotham Medium" }}>
            <i className="bi bi-geo-alt-fill"> </i>
            {data.school?.address || "Dirección no especificada"}
          </h6>
        </div>

        {isTutor ? (
          <TutorForm
            data={data}
            nicknameTooltipText={nicknameTooltipText}
            initialsTooltipText={initialsTooltipText}
            onSuccessSubmit={() => setReloadKey((prev) => prev + 1)}
            missingSizesData={missingSizes ? missingSizes : {}}
            isMobileView={isMobileView}
          />
        ) : (
          <>
            <div className={TutorFormStyles.infoBanner}>
              <i className="bi bi-info-circle-fill"></i>
              <span>
                Si querés modificar un talle, podes hacerlo desde tu cuenta blue!
              </span>
            </div>
            <CommonForm
              data={data}
              nicknameTooltipText={nicknameTooltipText}
              initialsTooltipText={initialsTooltipText}
              onSuccessSubmit={() => setReloadKey((prev) => prev + 1)}
              isMobileView={isMobileView}
            />
          </>
        )}
      </div>
    </>
  );
};

export default FormSizes;
